/**
 * 共有リンクのオーバーレイ。
 *
 * 招待の手段は場面で変わる。
 *   - その場に居る人  … QR を読ませるのが速い
 *   - 離れている人    … リンクを送る (共有 / コピー)
 * どれも「招待する」の一手なので、1 つのオーバーレイにまとめる。
 *
 * リンクは常に画面へ出すと情報量が多くなるだけなので、
 * グループ画面には出さず、押したときだけここに出す。
 *
 * ダイアログとして最低限守ること:
 *   - Escape で閉じる
 *   - 背景を押しても閉じる (中身を押したときは閉じない)
 *   - 開いたら閉じるボタンに焦点を移し、閉じたら元の要素に戻す
 */
import { useEffect, useRef, useState } from "react";
import { Icon } from "./Icon.js";
import { QrCode } from "./QrCode.js";

export interface ShareOverlayProps {
  /** 共有するリンク */
  url: string;
  /** グループ名。共有時の件名に使う */
  title: string;
  onClose: () => void;
}

/**
 * OS の共有シートが使えるか。
 * iOS / Android の主要ブラウザにはあるが、デスクトップには無いこともある。
 */
function canUseWebShare(): boolean {
  return typeof navigator !== "undefined" && typeof navigator.share === "function";
}

export function ShareOverlay({ url, title, onClose }: ShareOverlayProps) {
  const closeRef = useRef<HTMLButtonElement>(null);
  const [copied, setCopied] = useState(false);
  // 初回描画では判定しない (サーバー描画やテスト環境で差が出ないようにする)
  const [shareAvailable, setShareAvailable] = useState(false);

  useEffect(() => {
    setShareAvailable(canUseWebShare());
  }, []);

  useEffect(() => {
    // 開く前に focus していた要素へ戻せるよう覚えておく
    const previous = document.activeElement as HTMLElement | null;
    closeRef.current?.focus();

    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKeyDown);

    return () => {
      document.removeEventListener("keydown", onKeyDown);
      previous?.focus();
    };
  }, [onClose]);

  const copy = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // クリップボードが使えない環境では、表示しているリンクを選択してもらう
    }
  };

  const share = async (): Promise<void> => {
    try {
      await navigator.share({
        title: `${title} の割り勘`,
        text: `${title} の割り勘に参加してください`,
        url,
      });
    } catch (error: unknown) {
      // 共有シートを閉じただけ (AbortError) は失敗ではないので何もしない。
      // それ以外は共有できなかったので、代わりにコピーしておく。
      if (error instanceof Error && error.name === "AbortError") return;
      await copy();
    }
  };

  return (
    <div className="overlay" onClick={onClose}>
      <div
        className="overlay__panel"
        role="dialog"
        aria-modal="true"
        aria-label={`${title} の共有リンク`}
        // 背景のクリックだけで閉じたいので、中身への伝播を止める
        onClick={(event) => event.stopPropagation()}
      >
        <div className="overlay__head">
          <strong>共有リンク</strong>
          <button
            ref={closeRef}
            type="button"
            className="overlay__close"
            aria-label="閉じる"
            onClick={onClose}
          >
            <Icon name="close" size={18} />
          </button>
        </div>

        <code className="share-link">{url}</code>

        <div className="share-actions">
          <button type="button" className="button button--small" onClick={copy}>
            {copied ? (
              <>
                <Icon name="check" size={16} /> コピーしました
              </>
            ) : (
              <>
                <Icon name="copy" size={16} /> リンクをコピー
              </>
            )}
          </button>

          {/* 共有シートが無い環境ではボタンを出さない (押しても何も起きないため) */}
          {shareAvailable && (
            <button type="button" className="button button--ghost button--small" onClick={share}>
              <Icon name="share" size={16} /> 共有する
            </button>
          )}
        </div>

        <div className="share-qr">
          <QrCode value={url} size={200} label={`${title} の共有リンクの QR コード`} />
        </div>

        <p className="muted overlay__note">
          このリンクを知っている人は、ログインなしで支出の追加・編集ができます。
        </p>
      </div>
    </div>
  );
}
